import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Check, Loader2, Store, Clock, MapPin, CalendarDays, Ticket, CreditCard, Smartphone, Apple, PartyPopper, Lock } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/site";

const empty = {
  business_name: "",
  contact_name: "",
  email: "",
  phone: "",
  food_type: "",
  product_description: "",
  website: "",
  social_media: "",
  special_requests: "",
};

const SOLD_OUT = ["Food Vendor"];

const DETAILS = [
  { icon: CalendarDays, label: "Saturday, August 22, 2026" },
  { icon: MapPin, label: "Starr Park · 803 Forest Parkway, Forest Park, GA" },
  { icon: Clock, label: "Setup 12 Noon · Breakdown 5:00 PM" },
  { icon: Ticket, label: "$50 vending fee (bring your own tent, table & chairs)" },
];

export const VendorRegistration = () => {
  const [form, setForm] = useState(empty);
  const [cats, setCats] = useState([]);
  const [method, setMethod] = useState("cashapp");
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get("/vendors/status").then((r) => setInfo(r.data)).catch(() => {});
  }, []);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggle = (c) => setCats((p) => (p.includes(c) ? p.filter((x) => x !== c) : [...p, c]));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.business_name || !form.contact_name || !form.email || !form.phone || !form.product_description) {
      toast.error("Please fill in business name, contact, email, phone, and product description.");
      return;
    }
    if (cats.length === 0) {
      toast.error("Please select at least one vending category.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/vendors", { ...form, categories: cats, payment_method: method });
      setDone(true);
      toast.success("Registration received!");
      setForm(empty);
      setCats([]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const closed = info && !info.open;

  return (
    <section id="vendor" data-testid="vendor-section" className="relative py-24 sm:py-32 px-6 sm:px-10">
      <div className="max-w-5xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <p className="text-sm uppercase tracking-[0.3em] text-[#D4AF37] mb-5 flex items-center justify-center gap-2">
            <Store className="h-4 w-4" /> Community Event
          </p>
          <h2 className="font-display text-4xl sm:text-6xl font-semibold text-[#F9F6F0]">
            Become a <span className="gold-text italic">Vendor</span>
          </h2>
          <p className="text-[#A19CA8] text-lg mt-6 max-w-2xl mx-auto leading-relaxed">
            Join us for the Forest Park City Wide One Day Revival! Reserve your vending space below.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-5 gap-6 items-start">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-2 bg-[#130A1F]/70 border border-[#D4AF37]/25 p-7 lg:sticky lg:top-28"
          >
            <h3 className="font-display text-2xl text-[#D4AF37] mb-6">Event Details</h3>
            <ul className="space-y-4 mb-6">
              {DETAILS.map((d) => (
                <li key={d.label} className="flex items-start gap-3 text-[#A19CA8]">
                  <d.icon className="h-5 w-5 text-[#D4AF37] shrink-0 mt-0.5" />
                  <span className="text-sm leading-relaxed">{d.label}</span>
                </li>
              ))}
            </ul>
            <div className="border-t border-[#D4AF37]/15 pt-5">
              <p className="text-[#A19CA8] text-sm leading-relaxed">
                <span className="text-[#F9F6F0] font-semibold">Registration closes August 14, 2026</span> — no
                exceptions after close. Beverages must be <span className="text-[#D4AF37]">non-alcoholic only</span>.
              </p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-3 bg-[#130A1F]/70 border border-[#D4AF37]/25 p-7 sm:p-9"
          >
            {closed ? (
              <div className="text-center py-16" data-testid="vendor-closed">
                <Lock className="h-14 w-14 text-[#D4AF37] mx-auto mb-6" />
                <h3 className="font-display text-3xl text-[#F9F6F0] mb-3">Registration Closed</h3>
                <p className="text-[#A19CA8] max-w-sm mx-auto">
                  Vendor registration closed on August 14, 2026. Thank you for your interest — we hope to see you at the event!
                </p>
              </div>
            ) : done ? (
              <div className="text-center py-16" data-testid="vendor-success">
                <PartyPopper className="h-14 w-14 text-[#D4AF37] mx-auto mb-6" />
                <h3 className="font-display text-3xl text-[#F9F6F0] mb-3">You're Registered!</h3>
                <p className="text-[#A19CA8] mb-6 max-w-md mx-auto">
                  We've received your registration. Please send your <span className="text-[#D4AF37]">$50</span> vending fee to secure your spot:
                </p>
                <div className="space-y-3 max-w-xs mx-auto mb-8">
                  {method === "cashapp" && info && (
                    <div className="border border-[#D4AF37]/40 p-4">
                      <p className="text-xs uppercase tracking-widest text-[#A19CA8]">CashApp</p>
                      <p className="font-display text-2xl gold-text font-bold">{info.cashapp}</p>
                    </div>
                  )}
                  {method === "applepay" && info && (
                    <div className="border border-[#D4AF37]/40 p-4">
                      <p className="text-xs uppercase tracking-widest text-[#A19CA8]">Apple Pay</p>
                      <p className="font-display text-2xl gold-text font-bold">{info.applepay}</p>
                    </div>
                  )}
                </div>
                <button
                  onClick={() => setDone(false)}
                  data-testid="vendor-another-btn"
                  className="border-2 border-[#D4AF37] text-[#D4AF37] uppercase tracking-wider text-sm px-7 py-3 hover:bg-[#D4AF37]/10 transition-all"
                >
                  Register Another
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-5" data-testid="vendor-form">
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Business Name" required>
                    <input data-testid="vendor-business" value={form.business_name} onChange={update("business_name")} placeholder="Your business" className="ccc-input" />
                  </Field>
                  <Field label="Contact Name" required>
                    <input data-testid="vendor-contact" value={form.contact_name} onChange={update("contact_name")} placeholder="Your name" className="ccc-input" />
                  </Field>
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Email" required>
                    <input data-testid="vendor-email" type="email" value={form.email} onChange={update("email")} placeholder="you@email.com" className="ccc-input" />
                  </Field>
                  <Field label="Phone" required>
                    <input data-testid="vendor-phone" value={form.phone} onChange={update("phone")} placeholder="(404) 000-0000" className="ccc-input" />
                  </Field>
                </div>

                <div>
                  <label className="block text-sm uppercase tracking-[0.18em] text-[#D4AF37] font-semibold mb-3">
                    Vending Category <span>*</span>
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 max-h-56 overflow-y-auto pr-1" data-testid="vendor-categories">
                    {(info?.categories || []).map((c) => {
                      const soldOut = SOLD_OUT.includes(c);
                      const active = cats.includes(c);
                      return (
                        <button type="button" key={c} disabled={soldOut} onClick={() => !soldOut && toggle(c)}
                          data-testid={`cat-${c.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
                          className={`flex items-center gap-2.5 px-3.5 py-2.5 text-left text-sm border transition-all ${
                            soldOut
                              ? "border-[#D4AF37]/10 text-[#A19CA8]/40 cursor-not-allowed line-through decoration-[#A19CA8]/40"
                              : active
                              ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#F9F6F0]"
                              : "border-[#D4AF37]/20 text-[#A19CA8] hover:border-[#D4AF37]/50"
                          }`}>
                          <span className={`h-4 w-4 shrink-0 border flex items-center justify-center ${active && !soldOut ? "bg-[#D4AF37] border-[#D4AF37]" : "border-[#D4AF37]/50"}`}>
                            {active && !soldOut && <Check className="h-3 w-3 text-[#06040A]" />}
                          </span>
                          <span className="flex-1">{c}</span>
                          {soldOut && <span className="no-underline text-[10px] uppercase tracking-wider text-[#D4AF37]/70 border border-[#D4AF37]/30 px-1.5 py-0.5">Sold Out</span>}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <Field label="If Food/Beverage — what type?">
                  <input data-testid="vendor-food-type" value={form.food_type} onChange={update("food_type")} placeholder="e.g., BBQ, tacos, lemonade (non-alcoholic)" className="ccc-input" />
                </Field>
                <Field label="What will you be selling / offering?" required>
                  <textarea data-testid="vendor-products" value={form.product_description} onChange={update("product_description")} rows={3} placeholder="Describe your products or services..." className="ccc-input resize-none" />
                </Field>
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Website (optional)">
                    <input data-testid="vendor-website" value={form.website} onChange={update("website")} placeholder="https://..." className="ccc-input" />
                  </Field>
                  <Field label="Social Media (optional)">
                    <input data-testid="vendor-social" value={form.social_media} onChange={update("social_media")} placeholder="@yourhandle" className="ccc-input" />
                  </Field>
                </div>
                <Field label="Special Requests (optional)">
                  <input data-testid="vendor-requests" value={form.special_requests} onChange={update("special_requests")} placeholder="Anything we should know?" className="ccc-input" />
                </Field>

                <div>
                  <label className="block text-sm uppercase tracking-[0.18em] text-[#D4AF37] font-semibold mb-3">
                    Pay Your $50 Vending Fee
                  </label>
                  <div className="grid sm:grid-cols-2 gap-2.5" data-testid="vendor-payment-methods">
                    <PayOption active={method === "cashapp"} onClick={() => setMethod("cashapp")} icon={Smartphone} title="CashApp" sub="$thechangecenteratl" testid="pay-cashapp" />
                    <PayOption active={method === "applepay"} onClick={() => setMethod("applepay")} icon={Apple} title="Apple Pay" sub="404-326-0944" testid="pay-applepay" />
                  </div>
                  <p className="text-[#D4AF37] text-xs mt-3 font-semibold uppercase tracking-wide">
                    Payment is due at sign-up in order to be booked.
                  </p>
                  <p className="text-[#A19CA8]/80 text-xs mt-1.5">
                    Send $50 via {method === "cashapp" ? "CashApp ($thechangecenteratl)" : "Apple Pay (404-326-0944)"} to secure your spot.
                  </p>
                </div>

                <button type="submit" disabled={loading} data-testid="submit-vendor-btn"
                  className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-[#D4AF37] to-[#B5952F] text-[#06040A] font-semibold uppercase tracking-wider text-sm px-8 py-4 hover:from-[#F3D56D] hover:to-[#D4AF37] transition-all shadow-[0_0_20px_rgba(212,175,55,0.3)] disabled:opacity-60">
                  {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Submit Registration"}
                </button>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const PayOption = ({ active, onClick, icon: Icon, title, sub, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className={`flex flex-col items-center text-center gap-1.5 px-3 py-4 border transition-all ${active ? "border-[#D4AF37] bg-[#D4AF37]/10" : "border-[#D4AF37]/20 hover:border-[#D4AF37]/50"}`}>
    <Icon className={`h-6 w-6 ${active ? "text-[#D4AF37]" : "text-[#A19CA8]"}`} />
    <span className={`text-sm font-semibold ${active ? "text-[#F9F6F0]" : "text-[#A19CA8]"}`}>{title}</span>
    <span className="text-xs text-[#A19CA8]">{sub}</span>
  </button>
);

const Field = ({ label, required, children }) => (
  <div>
    <label className="block text-sm uppercase tracking-[0.18em] text-[#D4AF37] font-semibold mb-2">
      {label} {required && <span className="text-[#D4AF37]">*</span>}
    </label>
    {children}
  </div>
);
